import React, { useState } from 'react';
import { formatLocalYmd } from '../utils/timezoneUtils';

function parseLocalYmd(ymd: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function monthCells(year: number, month: number): Array<{ day?: number; ymd?: string }> {
  const first = new Date(year, month, 1);
  const pad = first.getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ day?: number; ymd?: string }> = [];
  for (let i = 0; i < pad; i += 1) cells.push({});
  for (let day = 1; day <= lastDate; day += 1) {
    cells.push({ day, ymd: formatLocalYmd(new Date(year, month, day)) });
  }
  return cells;
}

export function timeChipLabel(hhmm: string): string {
  const [hRaw, mRaw] = String(hhmm || '').split(':');
  const hour = parseInt(hRaw, 10);
  const minute = (mRaw || '00').slice(0, 2);
  if (Number.isNaN(hour)) return hhmm;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minute} ${ampm}`;
}

export function looksLikeTimeChip(value: string): boolean {
  const raw = String(value || '').trim();
  return /\b\d{1,2}:\d{2}\s*(am|pm)\b/i.test(raw) || /^([01]?\d|2[0-3]):[0-5]\d$/.test(raw);
}

export function TimeSlotGrid({
  times,
  textColor,
  disabled,
  onPick,
}: {
  times: string[];
  textColor: string;
  disabled?: boolean;
  onPick: (time: string, label: string) => void;
}) {
  if (times.length === 0) return null;
  const line = `${textColor}22`;
  return (
    <div className="grid grid-cols-3 gap-1.5 mt-2">
      {times.map((time) => {
        const label = looksLikeTimeChip(time) && /am|pm/i.test(time) ? time : timeChipLabel(time);
        return (
          <button
            key={time}
            type="button"
            disabled={disabled}
            className="text-xs bg-transparent border px-1.5 py-1.5 cursor-pointer disabled:opacity-60"
            style={{ borderColor: line, color: textColor, borderRadius: 8 }}
            onClick={() => onPick(time, label)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function DateChoicePicker({
  options,
  showCalendar,
  accentColor,
  accentInk,
  textColor,
  disabled,
  onPick,
}: {
  options: Array<{ label: string; date: string }>;
  showCalendar?: boolean;
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  onPick: (ymd: string, label: string) => void;
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const todayYmd = formatLocalYmd(new Date());
  const min = options[0]?.date && options[0].date <= todayYmd ? options[0].date : todayYmd;
  const minDate = parseLocalYmd(min);
  const [monthCursor, setMonthCursor] = useState(() => new Date(minDate.getFullYear(), minDate.getMonth(), 1));
  const muted = `${textColor}99`;
  const line = `${textColor}22`;
  const maxDate = new Date(minDate.getFullYear(), minDate.getMonth() + 12, minDate.getDate());

  const canPrev = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canNext = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1) < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  const cells = monthCells(monthCursor.getFullYear(), monthCursor.getMonth());
  const monthTitle = monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option.date}
            type="button"
            disabled={disabled}
            className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
            style={{ borderColor: line, color: textColor }}
            onClick={() => onPick(option.date, option.label)}
          >
            {option.label}
          </button>
        ))}
        {showCalendar ? (
          <button
            type="button"
            disabled={disabled}
            className="text-xs font-semibold border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
            style={
              calendarOpen
                ? { background: accentColor, color: accentInk, borderColor: accentColor }
                : { background: 'transparent', color: textColor, borderColor: line }
            }
            onClick={() => setCalendarOpen((open) => !open)}
          >
            Show Calendar
          </button>
        ) : null}
      </div>
      {showCalendar && calendarOpen && !disabled ? (
        <div className="mt-2 px-2 py-2" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              className="text-sm px-2 py-0.5 bg-transparent border-0 cursor-pointer disabled:opacity-30"
              style={{ color: textColor }}
              disabled={!canPrev}
              aria-label="Previous month"
              onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
            >
              ‹
            </button>
            <p className="m-0 text-sm font-semibold">{monthTitle}</p>
            <button
              type="button"
              className="text-sm px-2 py-0.5 bg-transparent border-0 cursor-pointer disabled:opacity-30"
              style={{ color: textColor }}
              disabled={!canNext}
              aria-label="Next month"
              onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-1 text-center">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="text-[10px] font-semibold" style={{ color: muted }}>{day}</div>
            ))}
            {cells.map((cell, index) => {
              if (!cell.ymd) return <div key={`e-${index}`} />;
              const tooEarly = cell.ymd < min;
              const tooLate = cell.ymd > formatLocalYmd(maxDate);
              const isToday = cell.ymd === todayYmd;
              return (
                <button
                  key={cell.ymd}
                  type="button"
                  disabled={tooEarly || tooLate}
                  className="text-xs h-7 rounded-full border-0 cursor-pointer disabled:opacity-30 disabled:cursor-default"
                  style={
                    isToday
                      ? { background: accentColor, color: accentInk }
                      : { background: 'transparent', color: textColor }
                  }
                  onClick={() => {
                    onPick(cell.ymd!, cell.ymd!);
                    setCalendarOpen(false);
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
